<script lang="ts">
  import type { ApiIdentity } from '$lib/api/types';
  import { popover } from '$lib/a11y/dismissible';
  import { theme, type ThemeChoice } from '$lib/state/theme.svelte';
  let { current } = $props<{ current: ApiIdentity }>();
  type ModeOption = { value: 'system' | 'light' | 'dark'; label: string };
  // Order matches the visual segmented switch: Light | System | Dark.
  const modeOptions: ModeOption[] = [
    { value: 'light', label: 'Light' },
    { value: 'system', label: 'System' },
    { value: 'dark', label: 'Dark' },
  ];
  // The "Dark" mode button covers both dark variants. The currently-selected
  // mode is 'dark' whenever the user has picked either dark grey or OLED.
  let selectedMode = $derived<'system' | 'light' | 'dark'>(
    theme.choice === 'oled' ? 'dark' : theme.choice,
  );
  let thumbIndex = $derived(selectedMode === 'light' ? 0 : selectedMode === 'system' ? 1 : 2);
  function pickMode(m: 'system' | 'light' | 'dark'): void {
    if (m !== 'dark') {
      theme.set(m);
      return;
    }
    // Dark button toggles between grey and OLED when clicked repeatedly.
    // First click from a non-dark mode lands on grey.
    if (theme.choice === 'dark') theme.set('oled');
    else theme.set('dark');
  }
  let open = $state(false);
  // Presentation only: guests have no namespace, tokens or MCP OAuth surface.
  let isMember = $derived(current.user_class === 'member');
</script>

<div class="relative">
  <button
    type="button"
    class="text-sm bg-zinc-100 hover:bg-zinc-200 px-2 sm:px-3 py-1.5 rounded max-w-[10rem] sm:max-w-none truncate inline-flex items-center"
    onclick={() => (open = !open)}
    aria-haspopup="menu"
    aria-expanded={open}
    title={current.name}
  >
    <span class="truncate">{current.name}</span>
    <span class="ml-0.5 shrink-0" aria-hidden="true">▾</span>
  </button>
  {#if open}
    <div
      class="absolute right-0 top-full mt-1 bg-white border border-zinc-200 rounded shadow-lg w-72 max-w-[calc(100vw-1.5rem)] z-10"
      role="menu"
      tabindex="-1"
      {@attach popover({ onClose: () => (open = false), autoFocus: false })}
    >
      <dl class="px-3 py-2 text-sm space-y-1.5">
        <div>
          <dt class="text-xs uppercase tracking-wide text-zinc-400">Name</dt>
          <dd class="text-zinc-900">{current.name}</dd>
        </div>
        <div>
          <dt class="text-xs uppercase tracking-wide text-zinc-400">Account</dt>
          <dd class="text-zinc-900" data-testid="user-class">
            {isMember ? 'Member' : 'Guest · read-only access to apps shared with you'}
          </dd>
        </div>
        {#if current.userNamespaceStub}
          <div>
            <dt class="text-xs uppercase tracking-wide text-zinc-400">Namespace</dt>
            <dd class="text-zinc-900 font-mono text-xs break-all">{current.userNamespaceStub}</dd>
          </div>
        {/if}
        <div>
          <dt class="text-xs uppercase tracking-wide text-zinc-400">Email</dt>
          <dd class="text-zinc-900 break-all">{current.email}</dd>
        </div>
        <div>
          <dt class="text-xs uppercase tracking-wide text-zinc-400">User ID</dt>
          <dd class="text-zinc-900 font-mono text-xs break-all">{current.sub}</dd>
        </div>
      </dl>
      <div class="border-t border-zinc-200"></div>
      {#if isMember}
        <a
          href="/settings/metrics"
          class="block px-3 py-2 text-sm hover:bg-zinc-50"
          onclick={() => (open = false)}>My app metrics</a
        >
        <a
          href="/settings/llm"
          class="block px-3 py-2 text-sm hover:bg-zinc-50"
          onclick={() => (open = false)}>LLM gateway</a
        >
        <a
          href="/settings/usage"
          class="block px-3 py-2 text-sm hover:bg-zinc-50"
          onclick={() => (open = false)}>App LLM usage</a
        >
        <a
          href="/settings/tokens"
          role="menuitem"
          class="block px-3 py-2 text-sm hover:bg-zinc-50"
          onclick={() => (open = false)}>API tokens</a
        >
        <a
          href="/settings/mcp"
          role="menuitem"
          class="block px-3 py-2 text-sm hover:bg-zinc-50"
          onclick={() => (open = false)}>MCP setup</a
        >
      {/if}
      <div class="border-t border-zinc-200"></div>
      <div class="px-3 py-2 flex items-center gap-2">
        <span class="text-xs uppercase tracking-wide text-zinc-400 shrink-0">Theme</span>
        <div
          class="relative grid grid-cols-3 bg-zinc-100 rounded-full p-0.5 text-xs flex-1"
          role="radiogroup"
          aria-label="Theme"
        >
          <!-- Sliding thumb. width = 1/3 of the track; translateX(0/100/200%)
               positions it under each segment. The thumb glides on
               `transform`. A separate sibling renders the blue Dark-mode
               outline so it can fade independently of the slide. -->
          <span
            aria-hidden="true"
            class="absolute top-0.5 bottom-0.5 left-0.5 rounded-full bg-white shadow-sm border border-zinc-200 pointer-events-none"
            style="width: calc((100% - 0.25rem) / 3); transform: translateX({thumbIndex *
              100}%); transition: transform 280ms cubic-bezier(0.32, 0.72, 0.18, 1);"
          ></span>
          <span
            aria-hidden="true"
            class="absolute top-0.5 bottom-0.5 left-0.5 rounded-full border border-blue-500 ring-1 ring-blue-500/40 pointer-events-none"
            style="width: calc((100% - 0.25rem) / 3); transform: translateX({thumbIndex *
              100}%); opacity: {selectedMode === 'dark'
              ? 1
              : 0}; transition: transform 280ms cubic-bezier(0.32, 0.72, 0.18, 1), opacity 220ms ease;"
            title={selectedMode === 'dark' ? 'Click again to toggle Grey / OLED' : undefined}
          ></span>
          {#each modeOptions as opt (opt.value)}
            <button
              type="button"
              role="radio"
              aria-checked={selectedMode === opt.value}
              title={opt.value === 'dark' ? 'Click again to toggle Grey / OLED' : undefined}
              class="relative z-10 px-2 py-1 rounded-full font-medium transition-colors {selectedMode ===
              opt.value
                ? 'text-zinc-900'
                : 'text-zinc-500 hover:text-zinc-700'}"
              onclick={() => pickMode(opt.value)}
            >
              {opt.label}
            </button>
          {/each}
        </div>
      </div>
    </div>
  {/if}
</div>
